import React, { useState, useEffect } from 'react';
import { useCookieConsentContext } from './CookieConsentProvider';
import { useLanguage } from '../../hooks/useLanguage';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({ isOpen, onClose }) => {
  const { consentState, acceptAll, rejectAll, acceptAnalytics } = useCookieConsentContext();
  const { t } = useLanguage();
  const [localConsent, setLocalConsent] = useState({
    necessary: true,
    analytics: consentState.analytics
  });

  // Reset local consent when modal opens or consent state changes
  useEffect(() => {
    if (isOpen) {
      setLocalConsent({
        necessary: true,
        analytics: consentState.analytics
      });
    }
  }, [isOpen, consentState.analytics]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (localConsent.analytics) {
      acceptAnalytics();
    } else {
      rejectAll();
    }
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleRejectAll = () => {
    rejectAll();
    onClose();
  };

  const handleCancel = () => {
    // Reset to original state without saving
    setLocalConsent({
      necessary: true,
      analytics: consentState.analytics
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {t('landing.cookieModal.title')}
                </h3>
                
                <div className="space-y-4">
                  {/* Cookie Necessari */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{t('landing.cookieModal.necessaryCookies')}</h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('landing.cookieModal.alwaysActive')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      {t('landing.cookieModal.necessaryDescription')}
                    </p>
                  </div>

                  {/* Cookie Analytics */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{t('landing.cookieModal.analyticsCookies')}</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={localConsent.analytics}
                          onChange={(e) => setLocalConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-600">
                      {t('landing.cookieModal.analyticsDescription')}
                    </p>
                  </div>
                </div>

                <div className="mt-6 text-xs text-gray-500">
                  <p>
                    {t('landing.cookieModal.moreInfo')}{' '}
                    <a 
                      href="/privacy-policy#cookies" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {t('landing.cookieModal.cookiePolicy')}
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:space-x-3 sm:space-x-reverse">
            <button
              type="button"
              onClick={handleSave}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('landing.cookieModal.savePreferences')}
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('landing.cookieModal.acceptAll')}
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('landing.cookieModal.rejectAll')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              {t('landing.cookieModal.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal; 